import { SITE_VOICE } from '../siteVoice';

export type FlowPhase =
  | 'idle'
  | 'keystroke'
  | 'terminal-encode'
  | 'pty-to-shell'
  | 'shell-process'
  | 'shell-output'
  | 'pty-to-terminal'
  | 'terminal-render'
  | 'done';

export const flowDiagramSteps: Array<{
  phase: FlowPhase;
  title: string;
  description: string;
  terminalContent: string[];
  highlight: 'keyboard' | 'terminal' | 'pty' | 'shell' | null;
  dataPacket?: string;
  dataDirection?: 'down' | 'up';
}> = [
  {
    phase: 'idle',
    title: 'Ready',
    description:
      'The terminal is waiting on my machine — cursor blinking in the project folder.',
    terminalContent: [`${SITE_VOICE.promptPath} $ ▌`],
    highlight: null,
  },
  {
    phase: 'keystroke',
    title: "You type 'ls'",
    description:
      'Each keystroke is a separate event sent to the terminal.',
    terminalContent: [`${SITE_VOICE.promptPath} $ ls▌`],
    highlight: 'keyboard',
    dataPacket: 'l s',
    dataDirection: 'down',
  },
  {
    phase: 'terminal-encode',
    title: 'Terminal encodes keystrokes',
    description:
      "The terminal converts your keystrokes into bytes: 'l' → 0x6C, 's' → 0x73",
    terminalContent: [`${SITE_VOICE.promptPath} $ ls▌`],
    highlight: 'terminal',
    dataPacket: '0x6C 0x73',
    dataDirection: 'down',
  },
  {
    phase: 'pty-to-shell',
    title: 'PTY forwards to shell',
    description:
      'The pseudo-terminal pipes the bytes to the shell process (like bash or zsh).',
    terminalContent: [`${SITE_VOICE.promptPath} $ ls▌`],
    highlight: 'pty',
    dataPacket: '0x6C 0x73',
    dataDirection: 'down',
  },
  {
    phase: 'shell-process',
    title: 'Shell receives and echoes',
    description:
      "The shell reads 'ls', echoes it back so you see what you typed, and waits for Enter.",
    terminalContent: [`${SITE_VOICE.promptPath} $ ls▌`],
    highlight: 'shell',
  },
  {
    phase: 'shell-output',
    title: "You press Enter → Shell runs 'ls'",
    description:
      "The shell executes 'ls', which lists files. The output is just text with escape codes for colors — here it's my repo and side folders.",
    terminalContent: [
      `${SITE_VOICE.promptPath} $ ls`,
      SITE_VOICE.flowLsOutputLine,
      `${SITE_VOICE.promptPath} $ ▌`,
    ],
    highlight: 'shell',
    dataPacket: '\\x1b[34mhow-terminals-work...',
    dataDirection: 'up',
  },
  {
    phase: 'pty-to-terminal',
    title: 'Output flows back through PTY',
    description:
      "The shell's output travels back through the PTY to the terminal.",
    terminalContent: [
      `${SITE_VOICE.promptPath} $ ls`,
      SITE_VOICE.flowLsOutputLine,
      `${SITE_VOICE.promptPath} $ ▌`,
    ],
    highlight: 'pty',
    dataPacket: '\\x1b[34mhow-terminals-work...',
    dataDirection: 'up',
  },
  {
    phase: 'terminal-render',
    title: 'Terminal renders output',
    description:
      'The terminal interprets escape sequences (\\x1b[34m = blue) and draws colored text to the grid.',
    terminalContent: [
      `${SITE_VOICE.promptPath} $ ls`,
      SITE_VOICE.flowLsOutputLine,
      `${SITE_VOICE.promptPath} $ ▌`,
    ],
    highlight: 'terminal',
  },
  {
    phase: 'done',
    title: 'Complete',
    description:
      'The full round trip: keystroke → encode → shell → execute → output → render. Repeat!',
    terminalContent: [
      `${SITE_VOICE.promptPath} $ ls`,
      SITE_VOICE.flowLsOutputLine,
      `${SITE_VOICE.promptPath} $ ▌`,
    ],
    highlight: null,
  },
];

export const flowDiagramUi = {
  stackLabel: 'Terminal Stack',
  outputLabel: 'Output',
  layers: {
    keyboard: { tag: '[kbd]', title: 'You (Keyboard)', subtitle: 'Physical keystrokes' },
    terminal: {
      tag: '[tty]',
      title: 'Terminal Emulator',
      subtitle: 'Encodes input, renders output',
    },
    pty: {
      tag: '[pty]',
      title: 'PTY (Pseudo-Terminal)',
      subtitle: 'Bidirectional pipe',
    },
    shell: {
      tag: '[sh]',
      title: 'Shell / Program',
      subtitle: 'bash, zsh — the shells I actually live in',
    },
  },
  controls: {
    previous: 'Previous',
    play: 'Play',
    playing: 'Playing...',
    next: 'Next',
  },
} as const;
